import clsx from "clsx";
import {
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    MenuSeparator,
    type MenuButtonProps,
    type MenuItemProps,
    type MenuItemsProps,
    type MenuProps,
    type MenuSeparatorProps,
} from "lib/headlessui/components/menu/menu";
import React from "react";
import { Button } from "./button";
import { Link } from "./link";

export function Dropdown(props: MenuProps) {
    return <Menu {...props} />;
}

export function DropdownButton<T extends React.ElementType = typeof Button>({
    as,
    ...props
}: { className?: string } & Omit<MenuButtonProps<T>, "className" | "as"> & {
        as?: T;
    }) {
    const Component = as ?? Button;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return <MenuButton {...(props as any)} as={Component} />;
}

export function DropdownMenu({
    anchor = "bottom",
    className,
    ...props
}: { className?: string } & Omit<MenuItemsProps, "as" | "className">) {
    return (
        <MenuItems
            {...props}
            transition
            anchor={anchor}
            className={clsx(
                className,
                // Anchor positioning
                "[--anchor-gap:--spacing(2)] [--anchor-padding:--spacing(1)] data-[anchor~=end]:[--anchor-offset:6px] data-[anchor~=start]:[--anchor-offset:-6px] sm:data-[anchor~=end]:[--anchor-offset:4px] sm:data-[anchor~=start]:[--anchor-offset:-4px]",
                // Base styles
                "isolate w-max rounded-xl p-1",
                // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
                "outline outline-transparent focus:outline-hidden",
                // Handle scrolling when menu won't fit in viewport
                "overflow-y-auto",
                // Popover background
                "bg-white/75 backdrop-blur-xl dark:bg-zinc-800/75",
                // Shadows
                "shadow-lg ring-1 ring-zinc-950/10 dark:ring-white/10 dark:ring-inset",
                // Define grid at the menu level if subgrid is supported
                "supports-[grid-template-columns:subgrid]:grid supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]",
                // Transitions
                "transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0",
            )}
        />
    );
}

export function DropdownItem({
    className,
    ...props
}: { className?: string } & (
    | Omit<MenuItemProps<"button">, "as" | "className">
    | Omit<MenuItemProps<typeof Link>, "as" | "className">
)) {
    const classes = clsx(
        className,
        // Base styles
        "group cursor-default rounded-lg px-3.5 py-2.5 focus:outline-hidden sm:px-3 sm:py-1.5",
        // Text styles
        "text-left text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]",
        // Focus
        "data-focus:bg-blue-500 data-focus:text-white",
        // Disabled state
        "data-disabled:opacity-50",
        // Forced colors mode
        "forced-color-adjust-none forced-colors:data-focus:bg-[Highlight] forced-colors:data-focus:text-[HighlightText] forced-colors:data-focus:*:data-[slot=icon]:text-[HighlightText]",
        // Use subgrid when available but fallback to an explicit grid layout if not
        "col-span-full grid grid-cols-[auto_1fr_1.5rem_0.5rem_auto] items-center supports-[grid-template-columns:subgrid]:grid-cols-subgrid",
        // Icons
        "*:data-[slot=icon]:col-start-1 *:data-[slot=icon]:row-start-1 *:data-[slot=icon]:mr-2.5 *:data-[slot=icon]:-ml-0.5 *:data-[slot=icon]:size-5 sm:*:data-[slot=icon]:mr-2 sm:*:data-[slot=icon]:size-4",
        "*:data-[slot=icon]:text-zinc-500 data-focus:*:data-[slot=icon]:text-white dark:*:data-[slot=icon]:text-zinc-400 dark:data-focus:*:data-[slot=icon]:text-white",
        // Avatar
        "*:data-[slot=avatar]:mr-2.5 *:data-[slot=avatar]:-ml-1 *:data-[slot=avatar]:size-6 sm:*:data-[slot=avatar]:mr-2 sm:*:data-[slot=avatar]:size-5",
    );

    return "href" in props ? (
        <MenuItem as={Link} {...props} className={classes} />
    ) : (
        <MenuItem as="button" type="button" {...props} className={classes} />
    );
}

export function DropdownDivider({
    className,
    ...props
}: { className?: string } & Omit<MenuSeparatorProps, "as" | "className">) {
    return (
        <MenuSeparator
            {...props}
            className={clsx(
                className,
                "col-span-full mx-3.5 my-1 h-px border-0 bg-zinc-950/5 sm:mx-3 dark:bg-white/10 forced-colors:bg-[CanvasText]",
            )}
        />
    );
}

export function DropdownLabel({
    className,
    ...props
}: React.ComponentPropsWithoutRef<"div">) {
    return (
        <div
            {...props}
            data-slot="label"
            className={clsx(className, "col-start-2 row-start-1")}
            {...props}
        />
    );
}
